import { useCallback, useMemo, useState } from 'react';

const RE_TEAM_ID = '60ebacf1eaabdd00175f2493';

const listingsWithCoordinates = (items) =>
    (items || []).filter((item) => item.latitude != null && item.longitude != null);

const mergeChannelIds = (listings, mapping) =>
    listings.map((item) => (mapping[item.id] ? { ...item, roomId: mapping[item.id] } : item));

export default function useGeoLocationRealEstate({
    realEstateListingsApi,
    realEstateMapIntentApi,
    mapIntentApi,
    chatroomsApi,
    toastWrapper,
}) {
    const [showRealEstate, setShowRealEstate] = useState(false);
    const [reListings, setReListings] = useState([]);
    const [nearbyVisitProperties, setNearbyVisitProperties] = useState([]);
    const [reFilters, setReFilters] = useState({});
    const [reBobConfirm, setReBobConfirm] = useState(null);
    const [reLoading, setReLoading] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);

    const loadListings = useCallback(
        async (filters = {}) => {
            setReLoading(true);
            try {
                const response = await realEstateListingsApi.getPublicListings({ page: 1, limit: 100, ...filters });
                const listings = listingsWithCoordinates(response?.results);
                setReListings(listings);

                // Create missing property channels in the background, then attach their room IDs.
                const ids = listings.map((item) => item.id).slice(0, 200);
                if (ids.length) {
                    realEstateListingsApi
                        .ensurePropertyChannels(ids)
                        .then((batchResponse) => {
                            const mapping = (batchResponse?.results || []).reduce((result, row) => {
                                if (row.roomId) result[row.propertyId] = row.roomId;
                                return result;
                            }, {});
                            setReListings((previous) => mergeChannelIds(previous, mapping));
                        })
                        .catch((error) => console.error('ensurePropertyChannels failed', error));
                }
            } catch (error) {
                toastWrapper.error(error?.message || 'Failed to load real estate listings');
            } finally {
                setReLoading(false);
            }
        },
        [realEstateListingsApi, toastWrapper]
    );

    const loadNearbyVisitProperties = useCallback(
        async ({ daysBack = 30, radius = 2 } = {}) => {
            try {
                const response = await realEstateListingsApi.getPropertiesNearVisits({ daysBack, radius });
                setNearbyVisitProperties(listingsWithCoordinates(response?.results));
            } catch (error) {
                console.error('Failed to load properties near visits', error);
            }
        },
        [realEstateListingsApi]
    );

    const toggleRealEstate = useCallback(async () => {
        const next = !showRealEstate;
        setShowRealEstate(next);
        if (next) {
            setReFilters({});
            setSelectedListing(null);
            await loadListings();
            loadNearbyVisitProperties();
        }
    }, [showRealEstate, loadListings, loadNearbyVisitProperties]);

    const handleSearch = useCallback(
        async (text, resolvedMapIntent = null) => {
            if (!text || !text.trim()) {
                setReFilters({});
                setReBobConfirm(null);
                await loadListings();
                return;
            }

            try {
                const result = resolvedMapIntent || (await mapIntentApi.resolveMapIntent(text));
                if (result?.category !== 'real_estate' || !result?.intent) {
                    setReFilters({ searchText: text });
                    setReBobConfirm(null);
                    await loadListings({ searchText: text });
                    return;
                }

                const filters = { ...result.filters };
                delete filters.category;
                if (result.confidence === 'high') {
                    setReFilters(filters);
                    setReBobConfirm(null);
                    const { searchText, ...structured } = { searchText: text, ...filters };
                    await loadListings(structured);
                } else if (result.confidence === 'medium' && result.confirmText) {
                    setReBobConfirm({ text: result.confirmText, pendingFilters: filters });
                } else {
                    setReFilters({ searchText: text });
                    await loadListings({ searchText: text });
                }
            } catch (error) {
                setReFilters({ searchText: text });
                await loadListings({ searchText: text });
            }
        },
        [loadListings, mapIntentApi]
    );

    const handlePropertyLabelChange = useCallback((propertyId, newLabel) => {
        const updateLabel = (listings) =>
            listings.map((item) => (item.id === propertyId ? { ...item, label: newLabel } : item));
        setReListings(updateLabel);
        setNearbyVisitProperties(updateLabel);
    }, []);

    const confirmReBob = useCallback(async () => {
        if (!reBobConfirm?.pendingFilters) return;
        setReFilters(reBobConfirm.pendingFilters);
        setReBobConfirm(null);
        await loadListings(reBobConfirm.pendingFilters);
    }, [reBobConfirm, loadListings]);

    const removeReFilter = useCallback(
        async (key) => {
            const next = { ...reFilters };
            delete next[key];
            setReFilters(next);
            setSelectedListing(null);
            await loadListings(next);
        },
        [reFilters, loadListings]
    );

    const openPropertyChannel = useCallback(
        async (item) => {
            try {
                if (item.roomId) {
                    window.location.href = `/teams/${item.roomId}`;
                    return;
                }
                const title = (item.address || '').trim() || `Property ${item.id}`;
                const found = await chatroomsApi.findRoomByTeamAndTitle(RE_TEAM_ID, title);
                const existing = (found?.results || [])[0];
                const roomId = existing?.id || existing?._id;
                if (roomId) {
                    window.location.href = `/teams/${roomId}`;
                } else {
                    toastWrapper.error('Channel not found for this property');
                }
            } catch (error) {
                toastWrapper.error(error?.message || 'Failed to open channel');
            }
        },
        [chatroomsApi, toastWrapper]
    );

    const filterChips = useMemo(
        () => realEstateMapIntentApi.buildFilterChips(reFilters),
        [reFilters, realEstateMapIntentApi]
    );

    return {
        showRealEstate,
        setShowRealEstate,
        reListings,
        nearbyVisitProperties,
        reFilters,
        setReFilters,
        reBobConfirm,
        setReBobConfirm,
        reLoading,
        selectedListing,
        setSelectedListing,
        loadListings,
        loadNearbyVisitProperties,
        toggleRealEstate,
        handleSearch,
        handlePropertyLabelChange,
        confirmReBob,
        removeReFilter,
        openPropertyChannel,
        filterChips,
    };
}
